/**
 * The words for the firm of a door and its portal, per calendar kind (E8), derived like the
 * brand's noun on the action list. In a customer's calendar every door is the customer's, so
 * the action's pages speak of »Kunde« and »Kalenderzugang«; with sponsors nothing changes.
 *
 * The German forms the sentences need, not a grammar: »Kunde« declines weakly (»des Kunden«,
 * »dem Kunden«), »Sponsor« does not.
 */
export interface Wording {
  /** »der Sponsor«, »der Kunde«. */
  readonly nominative: string;
  /** »des Sponsors«, »des Kunden«. */
  readonly genitive: string;
  /** »dem« and »den Sponsor«, »dem« and »den Kunden«. */
  readonly oblique: string;
  /** »Sponsoren«, »Kunden«: the tab of the firm list. */
  readonly plural: string;
  /** »Für Sponsoren«, »Für den Kunden«: the second table of the notification settings. */
  readonly audience: string;
  /** The heading of the firm's access: »Zugang zum Sponsorenportal«, »Kalenderzugang«. */
  readonly access: string;
  /** The portal in a sentence: »im Sponsorenportal«, »im Kalenderzugang«. */
  readonly portal: string;
  /** The tab of the firm list the firm's page belongs to (`?ansicht=`). */
  readonly view: "" | "?ansicht=kunden";
}

export const SPONSOR_WORDING: Wording = {
  nominative: "Sponsor",
  genitive: "Sponsors",
  oblique: "Sponsor",
  plural: "Sponsoren",
  audience: "Für Sponsoren",
  access: "Zugang zum Sponsorenportal",
  portal: "Sponsorenportal",
  view: "",
};

export const CUSTOMER_WORDING: Wording = {
  nominative: "Kunde",
  genitive: "Kunden",
  oblique: "Kunden",
  plural: "Kunden",
  audience: "Für den Kunden",
  access: "Kalenderzugang",
  portal: "Kalenderzugang",
  view: "?ansicht=kunden",
};

/** The words of an action: a customer's calendar is one with `customer_sponsor_id` (E4). */
export function wordingOf(action: { readonly customer_sponsor_id?: string | null }): Wording {
  return (action.customer_sponsor_id ?? null) === null ? SPONSOR_WORDING : CUSTOMER_WORDING;
}

/** The firm's page, under the tab the words belong to. */
export function firmPath(sponsorId: string, wording: Wording): string {
  return `/sponsoren/${sponsorId}${wording.view}`;
}
